import {dispatch} from 'd3-dispatch';
import {extent, merge} from 'd3-array';
import {local} from 'd3-selection';
import {scaleTime, scaleLinear, scalePow, scaleLog, schemeCategory10} from 'd3-scale';
import {timeFormatDefaultLocale} from 'd3-time-format';
import {formatDefaultLocale} from 'd3-format';

import {className, setMethodsFromAttrs, setMethodFromDefaultObj} from '../../modules/util';
import {comparator} from '../../modules/transform';

import absPos from './absPos';
import aggregate from './aggregate';
import aggregateMixed from './aggregateMixed';
import color from './color';
import condition from './condition';
import dehighlightNodes from './dehighlightNodes';
import dehighlightRegions from './dehighlightRegions';
import dimension from './dimension';
import dimensions from './dimensions';
import domain from './domain';
import facet from './facet';
import filterNodes from './filterNodes';
import filterRegions from './filterRegions';
import highlightNodes from './highlightNodes';
import highlightRegions from './highlightRegions';
import innerSize from './innerSize';
import legend from './legend';
import limitKeys from './limitKeys';
import limitRows from './limitRows';
import measure from './measure';
import measureName from './measureName';
import measures from './measures';
import mixedDimension from './mixedDimension';
import multiTooltip from './multiTooltip';
import nodeName from './nodeName';
import nodes from './nodes';
import offset from './offset';
import parent from './parent';
import rebindOnMethod from './rebindOnMethod';
import regions from './regions';
import render from './render';
import renderCanvas from './renderCanvas';
import renderFrame from './renderFrame';
import renderLegend from './renderLegend';
import renderRegion from './renderRegion';
import renderSpectrum from './renderSpectrum';
import renderTooltip from './renderTooltip';
import reset from './reset';
import size from './size';
import scale from './scale';
import setCustomDomain from './setCustomDomain';
import styleFont from './styleFont';
import tooltip from './tooltip';

function localeKR (locale) { // locale 설정
  locale = locale || 'ko-KR';
  let timeFormats = {
    'ko-KR': {
      'dateTime': '%Y/%m/%d %a %X',
      'date': '%Y/%m/%d',
      'time': '%H:%M:%S',
      'periods': ['오전', '오후'],
      'days': ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'],
      'shortDays': ['일', '월', '화', '수', '목', '금', '토'],
      'months': ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
      'shortMonths': ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월']
    }
  };
  let formats = {
    'ko-KR': {
      'decimal': '.',
      'thousands': ',',
      'grouping': [3],
      'currency': ['₩', '']
    }
  };
  timeFormatDefaultLocale(timeFormats[locale]);
  formatDefaultLocale(formats[locale]);
}

const defaultFont = {
  'font-family': 'sans-serif',
  'font-size': 12,
  'font-weight': 'lighter',
  'font-style': 'normal'
}

const continousColorScheme = ['#ece7f2','#2b8cbe'];
const categoryColorScheme = schemeCategory10;
const countMeasureTitle = '개수';
const countMeasure = {field: '__--daisy_count_measure--__', op: 'count'};
const mixedMeasure = {field: '__--daisy_mixed_measure--__', op: 'mean'};

const _attrs = {
  aggregated: false,
  axisToggle: [], //axis가 그려질지, grid가 있을지의 여부만 저장, 실제 axis설정은 __execs__에 적용
  axisTitles: [],
  color : categoryColorScheme,
  container: null,
  customDomain: null,
  data: [],
  dimensions: [],
  facet: null,
  font: defaultFont,
  grid: false,
  height: 480,
  highlightColor: '#fddd9b',
  label: null,
  legend: null,
  limitKeys : 200,
  limitRows: 1000,
  name: null,
  noAxisOffset: false, //axis 없다고 가정하여 offset 계산하기 위해
  nodeName: className('mark node', true),
  margin: {top: 40, right: 40, bottom: 40, left: 40},
  marginOffset: false,
  measures: [],
  parent : null,
  regionName: className('mark region', true),
  size: null,
  tooltip: true,
  transition: {duration: 600, delay: 20},
  width: 640,
  zeroOffset: false,
  zeroMargin: false
}

class Default {
  constructor(appendedAttrs) {
    localeKR();
    this.__attrs__ = JSON.parse(JSON.stringify(_attrs));
    this.__execs__ = {
      axis: {}, //실제 axis object는 __execs__에 저장
      condition: null, 
      canvas: null, 
      hidden: null,
      field: {}, // {x,y,color,raidus}
      legend:null, 
      mark: local(), //mark의 설정을 selection node와 연결
      scale: {},
      tooltip: null,
      regions: null,
      nodes: null
    }; 
    if (appendedAttrs) { //추가 attr가 있을 경우, 자식 인스턴스 생성시 사용;
      appendedAttrs = JSON.parse(JSON.stringify(appendedAttrs));
      for (let attr in appendedAttrs) {
        if(appendedAttrs.hasOwnProperty(attr)) {
          this.__attrs__[attr] = appendedAttrs[attr];
        }
      }
    }
    this.on = undefined; //.on 리스너를 초기화시킨다.
    this.__execs__.selectDispatch = dispatch('selectClick', 'selectEnter', 'selectLeave');
    this.rebindOnMethod(this.__execs__.selectDispatch);
  }
}

Default.continousDomain  = function (target, measure, level = 0, mixed = false, stacked = false, aggregated = false, padding = 0) {
  let curLevel = 0;
  let domain = [];
  let _value = function(target, curLevel) {
    target.forEach(function(d) {
      if(curLevel < level) {
        _value(d.children, curLevel+1);
      } else {
        if (measure === null || measure === undefined) {
          if (stacked) {
            domain.push(d.data.value);
            domain.push(d.data.value);
          } else {
            domain.push(d.data.value);
          }
        } else {
          let field = measure.field + (mixed || (measure.field === mixedMeasure.field && aggregated)? '' : '-' + measure.op);
          if (stacked) {
            domain.push(d.data.value[field + '-start'] );
            domain.push(d.data.value[field + '-end'] );
          } else {
            domain.push(d.data.value[field]);
          }
        }
      }
    });
  }
  _value(target, curLevel);  
  domain = extent(domain);
  if(padding <= 0) return domain;
  let dist = Math.abs(domain[0] - domain[1]);
  dist *= padding * 0.5;
  return [domain[0] - dist, domain[1] + dist];
}

Default.continousScale = function (domain, type = 'linear') { //해당 메소드 사용하여 continous 요소 설정
  let isTime = true;
  for (let i = 0; i < domain.length; i++) {
    let d = domain[i];
    if (!(d instanceof Date)) {
      isTime = false;
      break;
    }
  }
  if (isTime) {
    return scaleTime().domain(domain);
  } else {
    if (type === 'pow')  return scalePow().domain(domain);
    else if (type === 'log') return scaleLog().domain(domain);
    else return scaleLinear().domain(domain);
  }
}

Default.ordinalDomainFlatten = function (target, dimension) {
  let domain = [];
  target.forEach(d => {
    if(!domain.includes(d[dimension.field])) domain.push(d[dimension.field]);
  });
  domain.sort(comparator(dimension.order));
  return domain;
}

Default.ordinalDomain = function (target, level = 0, accessor = null, order = 'natural') { //FIXME: munged 된 상태에서 가져옴
  let domain = [];
  let curLevel = 0;
  let _keys = function (values, curLevel) {
    if (curLevel === level) {
      return values.map(accessor ? accessor : d => d.data.key);
    } else {
      return merge(values.map(d => _keys(d.children, curLevel + 1)));
    }
  }
  domain = _keys(target, curLevel);
  if (order && order !== 'natural') domain.sort(comparator(order));
  return domain;
}

setMethodFromDefaultObj(Default, 'font', defaultFont);
Default.prototype.absPos = absPos;
Default.prototype.aggregate = aggregate;
Default.prototype.aggregateMixed = aggregateMixed;
Default.prototype.color = color;
Default.prototype.condition = condition;
Default.prototype.dehighlightNodes = dehighlightNodes;
Default.prototype.dehighlightRegions = dehighlightRegions;
Default.prototype.dimension = dimension;
Default.prototype.dimensions = dimensions;
Default.prototype.domain = domain;
Default.prototype.facet = facet;
Default.prototype.filterNodes = filterNodes;
Default.prototype.filterRegions = filterRegions;
Default.prototype.highlightNodes = highlightNodes;
Default.prototype.highlightRegions = highlightRegions;
Default.prototype.innerSize = innerSize;
Default.prototype.legend = legend;
Default.prototype.limitKeys = limitKeys;
Default.prototype.limitRows = limitRows;
Default.prototype.mixedDimension = mixedDimension;
Default.prototype.measure = measure;
Default.prototype.measureName = measureName;
Default.prototype.measures = measures;
Default.prototype.multiTooltip = multiTooltip;
Default.prototype.nodeName = nodeName;
Default.prototype.nodes = nodes;
Default.prototype.offset = offset;
Default.prototype.parent = parent;
Default.prototype.tooltip = tooltip;
Default.prototype.scale = scale;
Default.prototype.styleFont = styleFont;
Default.prototype.rebindOnMethod = rebindOnMethod;
Default.prototype.regions = regions;
Default.prototype.render = render;
Default.prototype.renderCanvas = renderCanvas;
Default.prototype.renderFrame = renderFrame;
Default.prototype.renderLegend = renderLegend;
Default.prototype.renderRegion = renderRegion;
Default.prototype.renderSpectrum = renderSpectrum;
Default.prototype.renderTooltip = renderTooltip;
Default.prototype.reset = reset;
Default.prototype.size = size;
Default.prototype.setCustomDomain = setCustomDomain;

setMethodsFromAttrs(Default, _attrs);
export default Default;
export {countMeasure,countMeasureTitle,continousColorScheme, mixedMeasure};



// WEBPACK FOOTER //
// ../node_modules/daisy-chart/src/layouts/default/index.js